// scheduling/utils/reportRegistry.js
// The ONE list of reports in the Reporting section (Trello #309).
//
// Nav visibility, tab rendering, and the URL ?tab= vocabulary all read this, so a new
// report is one entry here + one handler in api/rprsReports + one seeded
// rprs.ReportAccess row. Nothing else needs editing — in particular AppShell does not,
// which is the whole point: the old inline `isSuperUser` nav check had to be edited by
// hand for every gated surface.
//
// METADATA ONLY — deliberately no component imports. This lives in utils/ and is
// imported by AppShell (which must not pull in report page code just to decide whether
// to draw a nav link). Reporting.jsx owns the tab -> component mapping.
//
// `key` MUST match a rprs.ReportAccess.ReportKey exactly. A mismatch means the report
// is silently unreachable: the probe returns keys, and anything not matched here is
// dropped. Keys follow admin-hq's namespaced convention (nav.* / report.* / feature.*)
// so the two apps stay legible to each other.
//
// `ready: false` means the access row and the URL slug are settled but the report is
// not built yet. Such entries are DOCUMENTED here and excluded from the tab strip, so
// nobody clicks a dead tab while the full vocabulary stays visible to the next dev.
// Flip one flag per report as it lands.

export const REPORTS = Object.freeze([
  {
    key: 'report.capacity',
    tab: 'capacity',
    label: 'Capacity',
    ready: true,
    // Listed first because it is the report that already existed in the main nav —
    // anyone who used it should find it immediately in its new home.
    description: '8-week scheduled-vs-capacity utilisation, by department → level → person.',
  },
  {
    key: 'report.gentrack',
    tab: 'gentrack',
    label: 'Gen Track Balance',
    ready: true,
    description: 'Tax vs Assurance split for General-track staff, against a 60/40 band.',
  },
  {
    key: 'report.signins',
    tab: 'signins',
    label: 'Sign-Ins',
    ready: true,
    description: 'Last sign-in and sign-in count per user.',
  },
  {
    key: 'report.adhoc',
    tab: 'adhoc',
    label: 'Ad-Hoc',
    ready: true,
    description: 'Pivot scheduled and charged hours by any dimension; export CSV.',
  },
  {
    key: 'report.terminated',
    tab: 'terminated',
    label: 'Terminated Staff',
    ready: true,
    // DJB, #249: "a view that only I can see where it shows any jobs scheduled on
    // terminated employees." Allowlisted to hstaggs + dbrock in rprs.ReportAccess,
    // same as report.signins.
    description: 'Scheduled hours still sitting on staff who no longer work here.',
  },
  {
    key: 'report.niches',
    tab: 'niches',
    label: 'Niche Involvement',
    ready: true,
    // Rachel Stanley (#726) oversees niche support; allowlisted in migrate-076.
    // Replaces the old Admin > Niche Interests tab, which showed rankings only.
    description: 'Ranked niche preferences and actual charged niche hours, per person.',
  },
  {
    key: 'report.lookahead',
    tab: 'lookahead',
    label: '4-Week Lookahead',
    ready: true,
    // DJB, 2026-09-29: "a weekly email for job managers showing the next 4 weeks' schedule
    // for jobs they manage" (#252 — the Empire "Job Scheduling Info" email). The Monday email
    // (api/rprsDigest + logic-apps/) is a thin reminder that deep-links HERE. Same access
    // shape as unscheduled: job_leads / my_jobs, super-users see the firm (migrate-079).
    // Listed before Unscheduled Hours so it is a job lead's default tab.
    description: 'Hours scheduled on the jobs you lead, week by week, with flags for time that may not belong there.',
  },
  {
    key: 'report.unscheduled',
    tab: 'unscheduled',
    label: 'Unscheduled Hours',
    ready: true,
    // Replaces the orphaned Empire LA logic-bot-cron-returnSchedulingReview (#252/#387).
    // The FIRST report with Scope='my_jobs' — a JM sees only the jobs they lead, super-users
    // see the firm (widened in the handler, NOT in the access row — see migrate-078).
    description: 'Return jobs whose prior-year prep hours are neither worked nor scheduled this year.',
  },
  {
    key: 'report.sva',
    tab: 'sva',
    label: 'Scheduled vs Actual',
    ready: true,
    // #252 PR 2 — the report-only revival of the Empire "Scheduled vs Actual" email. The
    // FIRST Audience='everyone' report (migrate-080): every staffer gets their own Me view,
    // so the Reporting nav now appears for everyone. Listed last so a job lead's default tab
    // stays the 4-Week Lookahead; for a plain staffer it is the only tab.
    description: 'What was on the calendar against what was charged, for the last 1 to 8 weeks.',
  },
]);

// tab slug -> entry, for resolving ?tab= without a linear scan at every render.
const BY_TAB = Object.freeze(
  REPORTS.reduce((acc, r) => { acc[r.tab] = r; return acc; }, {})
);

/** The registry entry for a ?tab= slug, or undefined. */
export function reportByTab(tab) {
  return BY_TAB[tab];
}

/**
 * Registry entries the caller may open AND that are built, in registry order.
 *
 * Intersects the server's answer with the registry rather than trusting either alone:
 * a key the server grants but the registry doesn't know is un-renderable, and a
 * registry entry the server didn't grant must not be shown. Registry order wins so tab
 * order is stable regardless of how the server returned the list.
 *
 * @param {string[]} grantedKeys from fetchMyReportAccess().reports
 */
export function accessibleReports(grantedKeys) {
  if (!Array.isArray(grantedKeys) || grantedKeys.length === 0) return [];
  const granted = new Set(grantedKeys);
  return REPORTS.filter(r => r.ready && granted.has(r.key));
}
